export function createAtlAttributionHeaderValue({
	atlWorkspaceId,
	tenantId,
	product,
}: {
	atlWorkspaceId?: string;
	tenantId: string;
	product: string;
}): string {
	const atlAttributionHeader = {
		atlWorkspaceId,
		tenantId,
		product,
	};
	return JSON.stringify(atlAttributionHeader);
}

/**
 * Parameters for building the atl-attribution header.
 *
 * @param cloudId - The cloud ID of the tenant (required)
 * @param product - The primary product, e.g., 'confluence', 'jira' (optional, defaults to 'platform')
 * @param activationId - The activation ID for the workspace (optional, from analyticsAttributes)
 */
export type BuildAtlAttributionHeadersParams = {
	cloudId?: string;
	product?: string;
	activationId?: string;
};

/**
 * Builds the atl-attribution headers object for API calls.
 *
 * Returns `undefined` if:
 * - `cloudId` is not provided
 *
 * Note: `cloudId` is required. `product` defaults to 'platform' if not provided.
 *
 * @example
 * ```typescript
 * const headers = buildAtlAttributionHeaders({
 *   cloudId: bootstrap.cloudId,
 *   product: bootstrap.primaryProduct,
 *   activationId: bootstrap.analyticsAttributes?.activationId,
 * });
 *
 * await someQuery({ variables, headers });
 * ```
 */
export function buildAtlAttributionHeaders({
	cloudId,
	product = 'platform',
	activationId,
}: BuildAtlAttributionHeadersParams): Record<string, string> | undefined {
	if (!cloudId) {
		return undefined;
	}

	const atlWorkspaceId = activationId
		? `ari:cloud:${product}:${cloudId}:workspace/${activationId}`
		: undefined;

	const headerValue = createAtlAttributionHeaderValue({
		...(atlWorkspaceId ? { atlWorkspaceId } : {}),
		tenantId: cloudId,
		product,
	});

	return {
		'atl-attribution': headerValue,
	};
}
